/**
 * Textos da audiometria que a ficha do trabalhador e o cartão da consulta escrevem do mesmo
 * jeito (#461). Nenhuma regra de critério mora aqui: situação, piora e médias chegam prontas do
 * servidor, e este arquivo só as escreve em português.
 */
import type { AudiometryComparison, AudiometryEar } from '../types/audiometry';

export const SEM_DATA = 'sem data';

export const ROTULO_DA_ORELHA: Record<AudiometryEar, string> = {
  right: 'Orelha direita',
  left: 'Orelha esquerda',
};

export function formatarHz(frequencia: number): string {
  return String(frequencia).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/**
 * Data sem hora ("2023-03-12") não passa por `Date`, que a deslocaria pelo fuso. O servidor manda
 * vazio ou nulo quando o atendimento não tem data legível: isso e qualquer texto que não vira
 * data aparecem como "sem data".
 */
export function formatarDia(valor: string | null | undefined): string {
  if (!valor) return SEM_DATA;
  const dia = /^(\d{4})-(\d{2})-(\d{2})/.exec(valor);
  if (dia) return `${dia[3]}/${dia[2]}/${dia[1]}`;
  const data = new Date(valor);
  return Number.isNaN(data.getTime()) ? SEM_DATA : data.toLocaleDateString('pt-BR');
}

function mesmoDiaLocal(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/**
 * "Salvo hoje às 14:32 por Dra. Helena Duarte". Sem `savedAt` o servidor ainda não informou
 * quando: fica o texto genérico. Data que não vira data aparece como "sem data".
 */
export function descreverSalvamento(
  savedAt: string | null | undefined,
  savedBy: { id: number; name: string } | null | undefined,
  agora: Date = new Date(),
): string {
  if (!savedAt) return 'Limiares salvos';
  const data = new Date(savedAt);
  const por = savedBy?.name ? ` por ${savedBy.name}` : '';
  if (Number.isNaN(data.getTime())) return `Salvo ${SEM_DATA}${por}`;

  const hora = `${String(data.getHours()).padStart(2, '0')}:${String(data.getMinutes()).padStart(2, '0')}`;
  const ontem = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate() - 1);
  if (mesmoDiaLocal(data, agora)) return `Salvo hoje às ${hora}${por}`;
  if (mesmoDiaLocal(data, ontem)) return `Salvo ontem às ${hora}${por}`;
  return `Salvo em ${data.toLocaleDateString('pt-BR')} às ${hora}${por}`;
}

/** Id chega como string do servidor e como number do modelo: compara pelo valor. */
export function mesmoId(a: number | string, b: number | string): boolean {
  return Number(a) === Number(b);
}

function listaDeHz(frequencias: number[]): string {
  const textos = frequencias.map(formatarHz);
  return textos.length > 1
    ? `${textos.slice(0, -1).join(', ')} e ${textos[textos.length - 1]}`
    : (textos[0] ?? '');
}

/**
 * "Orelha esquerda: 20 dB a mais em 4.000 e 6.000 Hz. Orelha direita: 15 dB a mais em 6.000 Hz."
 * Monta só o que o servidor marcou em `flagged`; a orelha que piorou mais vem primeiro.
 */
export function descreverPiora(flagged: AudiometryComparison['flagged']): string {
  const grupos: Array<{ ear: AudiometryEar; deltaDb: number; frequencias: number[] }> = [];
  for (const celula of flagged) {
    const grupo = grupos.find((g) => g.ear === celula.ear && g.deltaDb === celula.deltaDb);
    if (grupo) grupo.frequencias.push(celula.frequencyHz);
    else grupos.push({ ear: celula.ear, deltaDb: celula.deltaDb, frequencias: [celula.frequencyHz] });
  }
  const piorPorOrelha = (ear: AudiometryEar) =>
    Math.max(0, ...grupos.filter((g) => g.ear === ear).map((g) => g.deltaDb));
  grupos.sort((a, b) => piorPorOrelha(b.ear) - piorPorOrelha(a.ear) || b.deltaDb - a.deltaDb);

  return grupos
    .map((g) => `${ROTULO_DA_ORELHA[g.ear]}: ${g.deltaDb} dB a mais em ${listaDeHz(g.frequencias)} Hz.`)
    .join(' ');
}
